'use strict';

import { Box3, Group, MathUtils, Object3D, Vector2, Vector3 } from 'three';
import { SchematicBuildingModel } from './SchematicBuildingModel';
import { BaseElement } from '../../interfaces/BaseElement';
import { assignDeep, Buildings, projectMode, settingsData, threeJsVariables, TRoot } from '../../../settings';
import { concatPropsAndHighlights } from '../autoSelectionBoxes';
import { Building } from '../../building/Building';
import { removeSelectionBoxesLockColor } from '../../../functions';
import { searchGetResults } from '../../menu/search/search';
import { SchematicDraw } from './SchematicDraw/SchematicDraw';
import { hideBuildings } from '../../components/building-switch/building_switch';
import { DreamsPreloader } from '../../components/preloader/DreamsPreloader';
import { changeLangToCurrent } from '../../components/langSwitcher/langSwitcher';
import { setBuildingShape } from '../../crm/crm-requests';
import { Range } from '../../interfaces/switchers/Range';
import { DropDown } from '../../interfaces/DropDown';
import { Switch } from '../../interfaces/switchers/Switch';
import { BmbyMode } from '../../mode/BmbyMode';

const position = new Vector3();
const size = new Vector3();
const box = new Box3();

const ADVANCED_SETTINGS = {
	quantization: 10,
	globalRotation: 0,
	offset: 0,
	align: 'Center',
	fit: false,
	buildingsRotation: {}
};

export class SchematicBuilding {
	constructor (crm) {
		this.crm = JSON.parse(JSON.stringify(crm));
		this.type = 'square';
		this.prevType = 'square';
		this.shape = [];
		this.container = null;
		this.draw = null;
		this.advancedUI = null;
		this.selectionsGroup = new Group();
		this.selectionsGroup.name = 'Project Selection Boxes';
		this.advancedSettings = assignDeep({},ADVANCED_SETTINGS,config.features.liteMode.buildingAdvancedSettings || {});
		
		this.buildingGroup = new Group();
		this.buildingGroup.name = 'Project Buildings';
		
		this.buildingGroup.rotateY(this.advancedSettings.globalRotation);
		this.selectionsGroup.rotateY(this.advancedSettings.globalRotation);
		
		this.buildingNames = [...new Set(this.crm.map(apt => apt.buildingEngineId))];
		this.buildings = {};
		this.buildingsUI = {};
	}
	
	createUI() {
		this.container = new BaseElement()
			.addClass('schematic-building')
			.appendTo(TRoot)
			.DOMElement;
		
		this.draw = new SchematicDraw();
		
		const shapes = new BaseElement()
			.addClass('schematic-building__shapes')
			.event('change',this.#shapesHandler.bind(this))
			.appendTo(this.container)
			.DOMElement;
		
		this.advancedUI = new BaseElement('input')
			.addClass('schematic-building__advanced-input')
			.addAttribute('type','checkbox')
			.addAttribute('id','schematicAdvanced')
			.event('change',() => {
				if (this.draw.active) {
					this.draw.hide();
					this.#restorePreviouslyType();
				}
			})
			.appendTo(this.container)
			.DOMElement;
		
		new BaseElement('label')
			.addClass('schematic-building__advanced-label','language-string')
			.addAttribute('data-dictionary','Advanced')
			.addAttribute('for','schematicAdvanced')
			.innerHtml(changeLangToCurrent('Advanced'))
			.appendTo(this.container)
			.DOMElement;
		
		const advanced = new BaseElement()
			.addClass('schematic-building__advanced-content')
			.appendTo(this.container)
			.DOMElement;
		
		['Align','Fit','Offset','Quantization','Global rotation','Building rotation'].forEach(name => {
			const container = new BaseElement('fieldset')
				.addClass('schematic-building__advanced-element')
				.appendTo(advanced)
				.DOMElement;
			
			new BaseElement('legend')
				.addClass('schematic-building__advanced-element-text','language-string')
				.addAttribute('data-dictionary',name)
				.innerHtml(changeLangToCurrent(name))
				.appendTo(container);
			
			switch (name) {
				case 'Fit':
					const switcher = new Switch(container);
					switcher.checked = this.advancedSettings.fit;
					switcher.listener = (e) => this.advancedSettings.fit = e.target.checked;
					break;
				case 'Align':
					const selector = new DropDown(container,'schematic-building__select',{placeholder: true});
					selector.create();
					['Center','North','East','South','West','NorthWest','NorthEast','SouthEast','SouthWest'].forEach(facing => {
						const element = new BaseElement()
							.addClass('schematic-building__select-element','language-string')
							.event('click',() => {
								this.advancedSettings.align = facing;
								selector.changePlaceholder(changeLangToCurrent(facing));
							})
							.addAttribute('data-dictionary',facing)
							.innerHtml(changeLangToCurrent(facing))
							.DOMElement;
						selector.add(element);
						
						if (this.advancedSettings.align === facing) {
							selector.changePlaceholder(changeLangToCurrent(facing));
						}
					});
					break;
				case 'Offset':
					new Range(container,0,1,0.001,this.advancedSettings.offset)
						.addListener('input',(e) => {
							this.advancedSettings.offset = Number(e.target.value);
							outOffset.innerHtml(this.advancedSettings.offset);
						});
					
					const outOffset = new BaseElement('span')
						.addClass('schematic-building__advanced-element-out')
						.innerHtml(this.advancedSettings.offset)
						.appendTo(container);
					break;
				case 'Quantization':
					new Range(container,1,30,1,this.advancedSettings.quantization)
						.addListener('input',(e) => {
							this.advancedSettings.quantization = Number(e.target.value);
							outQuantization.innerHtml(this.advancedSettings.quantization);
						});
					
					const outQuantization = new BaseElement('span')
						.addClass('schematic-building__advanced-element-out')
						.innerHtml(this.advancedSettings.quantization)
						.appendTo(container);
					
					break;
				case 'Global rotation':
					new Range(container,0,Math.PI * 2,0.01,this.advancedSettings.globalRotation)
						.addListener('input',(e) => {
							this.advancedSettings.globalRotation = Number(e.target.value);
							this.buildingGroup.rotation.y = this.advancedSettings.globalRotation;
							this.selectionsGroup.rotation.y = this.advancedSettings.globalRotation;
							const deg = Math.round(MathUtils.radToDeg(this.advancedSettings.globalRotation));
							outGlobalRotation.innerHtml(deg);
						});
					
					const outGlobalRotation = new BaseElement('span')
						.addClass('schematic-building__advanced-element-out')
						.innerHtml(Math.round(MathUtils.radToDeg(this.advancedSettings.globalRotation)))
						.appendTo(container);
					
					break;
				case 'Building rotation':
					if (this.buildingNames.length >= 2) {
						const buildingsContainer = new BaseElement()
							.addClass('schematic-building__advanced__buildings')
							.appendTo(container)
							.DOMElement;
						
						this.buildingNames.forEach(buildingName => {
							const buildingContainer = new BaseElement()
								.addClass('schematic-building__advanced-element__building-container','hidden')
								.appendTo(buildingsContainer)
								.DOMElement;
							new BaseElement('span')
								.addClass('schematic-building__advanced-element__building-name')
								.innerHtml(buildingName)
								.appendTo(buildingContainer);
							
							this.buildingsUI[buildingName] = buildingContainer;
							const rotation = this.advancedSettings.buildingsRotation[buildingName] || 0;
							
							new Range(buildingContainer,0,Math.PI * 2,0.01,rotation)
								.addListener('input',(e) => {
									this.advancedSettings.buildingsRotation[buildingName] = Number(e.target.value);
									const deg = Math.round(MathUtils.radToDeg(this.advancedSettings.buildingsRotation[buildingName]));
									outBuilding.innerHtml(deg);
									
									const building = threeJsVariables.scene.getObjectByName(buildingName);
									const selections = threeJsVariables.scene.getObjectByName(buildingName + '_selectionBoxes');
									if (building) {
										building.rotation.y = this.advancedSettings.buildingsRotation[buildingName];
									}
									
									if (selections) {
										selections.rotation.y = this.advancedSettings.buildingsRotation[buildingName];
									}
								});
							
							const outBuilding = new BaseElement('span')
								.addClass('schematic-building__advanced-element-out')
								.innerHtml(Math.round(MathUtils.radToDeg(rotation)))
								.appendTo(buildingContainer);
						});
					} else {
						container.remove();
					}
					break;
			}
		});
		
		new BaseElement('button')
			.addClass('schematic-building__apply','language-string')
			.addAttribute('data-dictionary','Rebuild')
			.innerHtml(changeLangToCurrent('Rebuild'))
			.event('click',this.rebuild.bind(this))
			.appendTo(advanced);
		
		
		new BaseElement('button')
			.addClass('schematic-building__apply','language-string')
			.addAttribute('data-dictionary','Save shape')
			.event('click',this.#sendBuildingShape.bind(this))
			.innerHtml(changeLangToCurrent('Save shape'))
			.appendTo(this.container);
		
		['square','rectangle','circle','corner','custom'].forEach((type,index) => {
			const input = new BaseElement('input')
				.addClass('schematic-building__shape-input')
				.addAttribute('id',`shape_${type}`)
				.addAttribute('name','shape')
				.addAttribute('data-type',type)
				.addAttribute('type','radio')
				.appendTo(shapes);
			new BaseElement('label')
				.addClass('schematic-building__shape',type === 'custom' ? 'icon-pencil' : null)
				.addAttribute('data-type',type)
				.addAttribute('for',`shape_${type}`)
				.appendTo(shapes);
			
			// if (index < 4 ) {
			// 	new BaseElement()
			// 		.addClass('schematic-building__separator')
			// 		.appendTo(shapes);
			// }
			
			if (type === 'custom') {
				input.event('click',this.#drawHandler.bind(this));
			}
			
			if (type === this.type) {
				input.addAttribute('checked',true);
			}
		});
		
		this.updateBuildingsRotationUI();
	}
	
	updateBuildingsRotationUI() {
		const activeBuilding = Buildings.getActiveBuilding();
		Object.entries(this.buildingsUI).forEach(([name,ui]) => {
			if (activeBuilding && activeBuilding.name === name) {
				ui.classList.remove('hidden');
			} else {
				ui.classList.add('hidden');
			}
		});
	}
	
	rebuild() {
		const preloader = new DreamsPreloader(document.body);
		preloader
			.absolute()
			.substrate()
			.blur()
			.show();
		preloader.element.style.left = 0;
		preloader.element.style.top = 0;
		preloader.element.style.zIndex = 20000;
		
		setTimeout(() => {
			this.create();
			
			const selections = [];
			const prevActiveBuilding = Buildings.getActiveBuilding();
			
			Object.entries(this.buildings).forEach(([name,data]) => {
				selections.push(data.selectionsGroup);
				const model = data.buildingGroup.children[0];
				model.layers.set(0);
				// model.material.map = texture;
				const building = new Building(model);
				const modelGroup = building.create();
				data.buildingGroup.removeFromParent();
				this.buildingGroup.add(modelGroup);
				building.amenities.Roof = new Object3D();
				threeJsVariables.slicer.add(model);
				building.deactivate();
			});
			
			concatPropsAndHighlights({data:JSON.parse(JSON.stringify(this.crm))},selections,{data: config.crmProjectSettings});
			hideBuildings();
			
			if (prevActiveBuilding) {
				projectMode.Cylinder.switchBuilding(prevActiveBuilding.name);
			}
			
			preloader.remove();
		},10);
	}
	
	setShape(points) {
		if (Array.isArray(points)) {
			this.shape = points.map(arr => new Vector2().fromArray(arr));
		}
	}
	
	#sendBuildingShape() {
		const data = {
			type: this.type,
			advanced: this.advancedSettings,
			shape: this.type === 'custom' ? this.shape.map(vec => vec.toArray()) : []
		};
		
		setBuildingShape(data).then(() => {
			if (!BmbyMode.active)
				this.container?.remove()
		});
	}
	
	#restorePreviouslyType() {
		this.type = this.prevType;
		const prevElement = this.container.querySelector(`.schematic-building__shape-input[data-type="${this.type}"]`);
		if (prevElement) {
			prevElement.checked = true;
		}
	}
	
	async #drawHandler(e) {
		e.preventDefault();
		setTimeout(() => {
			if (!this.draw.active) {
				e.target.checked = true;
				this.#shapesHandler(e);
			} else {
				this.draw.hide();
				this.#restorePreviouslyType();
			}
		},0);
	}
	
	async #shapesHandler(e) {
		this.type = e.target.getAttribute('data-type');
		
		if (this.type === 'custom') {
			this.advancedUI.checked = false;
			this.draw.show(this.container);
			this.shape = await this.draw.getPoints();
			this.draw.clear();
			this.draw.hide();
		} else {
			this.draw.hide();
			this.shape = [];
		}
		
		this.rebuild();
		this.prevType = this.type;
	}
	
	create() {
		const numberInRow = 4;
		let count = 0;
		let offsetZ = 0;
		let offset = 10;
		let maxSpace = 0;
		position.set(0,0,0);
		
		this.dispose();
		this.buildingNames.forEach(building => {
			const props = this.crm.filter(apt => apt.buildingEngineId === building);
			const buildingScope = new SchematicBuildingModel(JSON.parse(JSON.stringify(props)),this);
			buildingScope.selectionsGroup.position.copy(position);
			buildingScope.buildingGroup.position.copy(position);
			
			box.setFromObject(buildingScope.buildingGroup);
			box.getSize(size);
			
			const max = Math.max(size.x,size.z);
			maxSpace = Math.max(max,maxSpace);
			
			if (count === numberInRow - 1) {
				offsetZ += maxSpace + offset;
				position.setX(0);
				position.setZ(offsetZ);
				count = -1;
			} else {
				position.setX(position.x + maxSpace + offset);
			}
			
			count++;
			
			this.selectionsGroup.add(buildingScope.selectionsGroup);
			this.buildingGroup.add(buildingScope.buildingGroup);
			
			this.buildings[building] = buildingScope;
		});
	}
	
	dispose() {
		if (this.buildingGroup.children.length) {
			let meshes = [];
			this.buildingGroup.traverse(el => {
				if (el.isMesh) {
					meshes.push(el);
				}
			});
			this.selectionsGroup.traverse(el => {
				if (el.isMesh) {
					meshes.push(el);
				}
			});
			
			while (this.buildingGroup.children.length) {
				this.buildingGroup.children[0].removeFromParent();
			}
			
			while (this.selectionsGroup.children.length) {
				this.selectionsGroup.children[0].removeFromParent();
			}
			
			meshes.forEach(mesh => {
				mesh.geometry.dispose();
				mesh.material.dispose();
			});
			
			meshes = null;
			
			this.buildings = {};
		}
	}
	
}

